import { ApiResponse } from "@/types";
import { getMSBaseUrl } from "@/utils/baseUrls";
import { MICRO_SERVICES } from "@/utils/enums";
import { keysToSnake } from "@/utils/snakeCaseConverter";

import {
  GroupedVersion,
  GroupedWorkflowVersionResponseSchema,
  SaveWorkflowSchema,
  WFActiveUserResponseSchema,
  WFEditRequestResponse,
  WorkflowResponseSchema,
  WorkflowStatusResponseSchema,
  WorkflowVersionResponseSchema,
  WorkflowVersionSchema,
  WorkflowEvent,
} from "../types/workflow-types";

import { workflowEditorApi } from "./api";
import { WorkflowEndpoints } from "./api-endpoints";
import { handleRenameWorkflowVersion } from "./workflow-api-utils";

const groupVersionsBySuccess = (
  versions: WorkflowVersionSchema[]
): GroupedVersion[] => {
  const groupedVersions: GroupedVersion[] = [];
  let currentGroup: GroupedVersion | null = null;

  for (const version of versions) {
    if (version.status === "success") {
      // Push the previous group if it exists
      if (currentGroup) {
        groupedVersions.push(currentGroup);
      }

      // Start a new group
      currentGroup = {
        ...version,
        children: [],
      };
    } else {
      if (currentGroup) {
        // Add to current group's children if we have a group
        currentGroup.children?.push(version);
      } else {
        // If no group exists yet, add as a standalone version
        groupedVersions.push({
          ...version,
          children: [],
        });
      }
    }
  }

  // Push the last group if exists
  if (currentGroup) {
    // Only include children if there are any
    if (currentGroup.children?.length === 0) {
      delete currentGroup.children;
    }
    groupedVersions.push(currentGroup);
  }

  return groupedVersions;
};

const debug = true;
// Define interface for Workflow Events pagination
interface WorkflowEventPagination {
  count: number;
  next: string | null;
  previous: string | null;
}

// Match the API response structure
interface WorkflowEventsResponse {
  results: {
    events: WorkflowEvent[];
  };
  pagination?: WorkflowEventPagination;
}

export const workflowManagerApi = workflowEditorApi.injectEndpoints({
  // TODO : add Tags to each endpoint
  endpoints: (build) => ({
    getWFActiveUser: build.query<
      WFActiveUserResponseSchema,
      { analysisId: string }
    >({
      query: ({ analysisId }) => ({
        url: WorkflowEndpoints.wfEditAccess(analysisId),
        method: "GET",
        ignoreBaseUrl: true,
      }),
    }),
    requestEditAccess: build.mutation<
      WFEditRequestResponse,
      { analysisId: string }
    >({
      query: ({ analysisId }) => ({
        url: WorkflowEndpoints.wfEditAccess(analysisId),
        method: "PUT",
        ignoreBaseUrl: true,
        data: {
          action: "claim",
        },
      }),
    }),
    endEditAccess: build.mutation<
      WFEditRequestResponse,
      { analysisId: string }
    >({
      query: ({ analysisId }) => ({
        url: WorkflowEndpoints.wfEditAccess(analysisId),
        method: "PUT",
        ignoreBaseUrl: true,
        data: {
          action: "end",
        },
      }),
    }),
    getworkflowsFromAnalysis: build.query<
      ApiResponse<WorkflowResponseSchema>,
      { analysisId: string }
    >({
      query: ({ analysisId }) => ({
        url: WorkflowEndpoints.getWorkflowFromAnalysis(analysisId),
        method: "GET",
        ignoreBaseUrl: true,
      }),
      keepUnusedDataFor: 0,
    }),
    createWorkflow: build.mutation<
      ApiResponse<WorkflowResponseSchema>,
      { analysisId: string }
    >({
      query: ({ analysisId }) => {
        return {
          url: WorkflowEndpoints.createWorkflowFromAnalysis(analysisId),
          method: "POST",
          ignoreBaseUrl: true,
          headers: {
            "client-id": import.meta.env.VITE_CLIENT_ID,
          },
          data: {
            analysis_id: analysisId,
            user_id: import.meta.env.VITE_USER_ID,
          },
        };
      },
    }),
    getWorkflow: build.query<
      ApiResponse<WorkflowResponseSchema>,
      { workflowId: string; wfRunId?: string, isPreview?: string }
    >({
      query: ({ workflowId, wfRunId, isPreview }) => ({
        url: WorkflowEndpoints.getWorkflow(workflowId), 
        method: "GET",
        ignoreBaseUrl: true,
        params: {
          ...(wfRunId && { workflow_run_id: wfRunId }),
          ...(isPreview && { is_a_preview_request: isPreview }),
        },
      }),
      keepUnusedDataFor: 0,
      providesTags: (result, error, { workflowId, wfRunId }) =>
        wfRunId
          ? [{ type: "wf-versions", id: wfRunId }]
          : [{ type: "Workflows", id: workflowId }],
    }),
    saveWorkflow: build.mutation<
      ApiResponse<WorkflowResponseSchema>,
      {
        analysisId: string;
        workflowId: string;
        workflow: any;
      }
    >({
      query: ({ analysisId, workflowId, workflow }) => ({
        url: WorkflowEndpoints.saveWorkflow(workflowId),
        method: "PUT",
        data: {
          analysis_id: analysisId,
          user_id: import.meta.env.VITE_USER_ID,
          ...keysToSnake(workflow),
        },
        headers: {
          "client-id": import.meta.env.VITE_CLIENT_ID,
        },
        ignoreBaseUrl: true,
      }),
    }),
    markOutput: build.mutation<
      ApiResponse<WorkflowResponseSchema>,
      {
        analysisId: string;
        workflowId: string;
        workflow: SaveWorkflowSchema;
      }
    >({
      query: ({ analysisId, workflowId, workflow }) => ({
        url: WorkflowEndpoints.markOutput(workflowId),
        method: "PUT",
        data: {
          analysis_id: analysisId,
          user_id: import.meta.env.VITE_USER_ID,
          ...keysToSnake(workflow),
        },
        headers: {
          "client-id": import.meta.env.VITE_CLIENT_ID,
        },
        ignoreBaseUrl: true,
      }),
    }),
    unMarkOutput: build.mutation<
      ApiResponse<WorkflowResponseSchema>,
      {
        analysisId: string;
        workflowId: string;
        workflow: any;
      }
    >({
      query: ({ analysisId, workflowId, workflow }) => ({
        url: WorkflowEndpoints.unMarkOutput(workflowId),
        method: "PUT",
        data: {
          analysis_id: analysisId,
          user_id: import.meta.env.VITE_USER_ID,
          ...keysToSnake(workflow),
        },
        headers: {
          "client-id": import.meta.env.VITE_CLIENT_ID,
        },
        ignoreBaseUrl: true,
      }),
    }),
    runWorkflow: build.mutation<
      ApiResponse<WorkflowResponseSchema>,
      { analysisId: string; workflowId: string; workflow: any }
    >({
      query: ({ analysisId, workflowId, workflow }) => ({
        url: WorkflowEndpoints.runWorkflow(workflowId),
        method: "PUT",
        data: {
          analysis_id: analysisId,
          user_id: import.meta.env.VITE_USER_ID,
          ...keysToSnake(workflow),
        },
        headers: {
          "client-id": import.meta.env.VITE_CLIENT_ID,
        },
        ignoreBaseUrl: true,
      }),
    }),
    getWorkflowRunStatus: build.query<
      ApiResponse<WorkflowStatusResponseSchema>,
      { workflowId: string; workflowRunId: string }
    >({
      query: ({ workflowId, workflowRunId }) => ({
        url: WorkflowEndpoints.getWorkflowStatus(workflowId, workflowRunId),
        method: "GET",
        ignoreBaseUrl: true,
      }),
      keepUnusedDataFor: 0,
    }),
    terminateWorkflow: build.mutation<
      ApiResponse<WorkflowResponseSchema>,
      { workflowId: string; workflowRunId: string }
    >({
      query: ({ workflowId, workflowRunId }) => ({
        url: WorkflowEndpoints.terminateWorkflow(workflowId, workflowRunId),
        method: "POST",
        ignoreBaseUrl: true,
      }),
    }),
    fixWorkflow: build.mutation<
      ApiResponse<WorkflowResponseSchema>,
      { analysisId: string; workflowId: string; workflow: any }
    >({
      query: ({ analysisId, workflowId, workflow }) => ({
        url: WorkflowEndpoints.fixWorkflow(workflowId),
        method: "PUT",
        data: {
          analysis_id: analysisId,
          user_id: import.meta.env.VITE_USER_ID,
          ...keysToSnake(workflow),
        },
        headers: {
          "client-id": import.meta.env.VITE_CLIENT_ID,
        },
        ignoreBaseUrl: true,
      }),
    }),
    runNode: build.mutation<
      ApiResponse<WorkflowResponseSchema>,
      {
        analysisId: string;
        workflowId: string;
        executionType: string;
        startNodeId: string;
        workflow: any;
      }
    >({
      query: ({
        analysisId,
        workflowId,
        executionType,
        startNodeId,
        workflow,
      }) => ({
        url: WorkflowEndpoints.runNode(workflowId, executionType, startNodeId),
        method: "PUT",
        data: {
          analysis_id: analysisId,
          user_id: import.meta.env.VITE_USER_ID,
          ...keysToSnake(workflow),
        },
        headers: {
          "client-id": import.meta.env.VITE_CLIENT_ID,
        },
        ignoreBaseUrl: true,
      }),
    }),
    terminateNodeRun: build.mutation<
      ApiResponse<string>,
      { workflowId: string; workflowNodeId: string }
    >({
      query: ({ workflowId, workflowNodeId }) => ({
        url: WorkflowEndpoints.terminateNodeRun(workflowId, workflowNodeId),
        method: "POST",
        ignoreBaseUrl: true,
      }),
    }),
    getWorkflowEvents: build.query<
      ApiResponse<WorkflowEventsResponse>,
      { analysisId: string; workflowId: string; url?: string | null }
    >({
      query: ({ analysisId, workflowId, url }) => {
        let modifiedUrl;
        if (url) modifiedUrl = getMSBaseUrl(MICRO_SERVICES.WORKFLOW_MANAGER) + url;
        
        return {
          url: modifiedUrl ?? WorkflowEndpoints.getWorkflowEvents(analysisId, workflowId, 25, 0),
          method: "GET",
          ignoreBaseUrl: true,
        };
      },
      // Provide a tag for cache invalidation
      providesTags: (_result, _error, { workflowId }) => [
        { type: "Workflows" as const, id: workflowId }
      ],
      transformResponse: (response: any) => {
        // Extract events from the correct path in the response
        let eventsFromApi: any[] = [];
        let paginationData: WorkflowEventPagination = {
          count: 0,
          next: null,
          previous: null
        };
        
        try {
          // The actual API response structure is { data: { events: [...] }, pagination: {...} }
          if (response?.data?.events) {
            eventsFromApi = response.data.events;
            if (response.pagination) {
              paginationData = response.pagination;
            }
          }
          // Fallback for other possible structures
          else if (response?.response?.data?.events) {
            eventsFromApi = response.response.data.events;
            if (response.response.pagination) {
              paginationData = response.response.pagination;
            }
          }
          else if (response?.response?.data?.results?.events) {
            eventsFromApi = response.response.data.results.events;
            if (response.response.pagination) {
              paginationData = response.response.pagination;
            }
          }
        } catch (error) {
          console.error('Error extracting events from response:', error);
        }
        
        // Format events for consistency
        const formattedEvents = Array.isArray(eventsFromApi) 
          ? eventsFromApi.map((apiEvent: any) => ({
              eventName: apiEvent.event_name || apiEvent.eventName,
              eventMessage: apiEvent.event_message || apiEvent.eventMessage,
              eventResolutionStatus: apiEvent.event_resolution_status || apiEvent.eventResolutionStatus,
              eventType: apiEvent.event_type || apiEvent.eventType,
              createdOn: apiEvent.created_on || apiEvent.createdOn
            }))
          : [];
          
        // Format the response to match expected structure in the component
        return {
          ...response,
          response: {
            ...response.response,
            data: {
              ...response.response?.data,
              results: {
                events: formattedEvents
              }
            },
            pagination: paginationData
          }
        };
      },
      merge: (existingData, newData, { arg }) => {
        if (!existingData || arg.url === null) {
          return newData;
        }
        
        // Get existing events
        const existingEvents = existingData.response.data?.results?.events || [];
        // Get new events
        const newEvents = newData.response.data?.results?.events || [];
        
        // Merge events and update pagination - follow the same pattern as getFlows
        return {
          ...newData,
          response: {
            ...newData.response,
            pagination: {
              count: newData.response.pagination!.count,
              next: newData.response.pagination!.next,
              previous: newData.response.pagination!.previous,
            },
            data: {
              ...newData.response.data,
              results: {
                events: [...existingEvents, ...newEvents]
              }
            }
          }
        };
      },
      serializeQueryArgs: ({ queryArgs }) => {
        // Use workflowId as the cache key
        return queryArgs.workflowId;
      },
      keepUnusedDataFor: 0,
    }),
    clearWorkflowEvents: build.mutation<
      ApiResponse<any>,
      { analysisId: string; workflowId: string }
    >({
      query: ({ analysisId, workflowId }) => ({
        url: WorkflowEndpoints.getWorkflowEvents(analysisId, workflowId),
        method: "PUT",
        ignoreBaseUrl: true,
        data: {
          action: "clear_all"
        },
      }),
      // Invalidate the getWorkflowEvents query cache when events are cleared
      invalidatesTags: (_result, _error, { workflowId }) => [
        { type: "Workflows" as const, id: workflowId }
      ],
    }),
    getWorkflowVersions: build.query<
      ApiResponse<GroupedWorkflowVersionResponseSchema>,
      { workflowId: string; url?: string | null }
    >({
      query: ({ workflowId, url }) => {
        let modifiedUrl;
        if (url)
          modifiedUrl = getMSBaseUrl(MICRO_SERVICES.WORKFLOW_MANAGER) + url;
        return {
          url: modifiedUrl ?? WorkflowEndpoints.getWorkflowVersions(workflowId),
          method: "GET",
          ignoreBaseUrl: true,
        };
      },
      keepUnusedDataFor: 0,
      providesTags: ["wf-versions"],
      serializeQueryArgs: ({ queryArgs }) => {
        return "wf-versions-" + queryArgs.workflowId;
      },
      transformResponse: (
        response: ApiResponse<WorkflowVersionResponseSchema>
      ) => {
        return {
          ...response,
          response: {
            ...response.response,
            data: {
              runs: groupVersionsBySuccess(response.response.data?.runs || []),
            },
          },
        };
      },
      merge: (currentCache, newItems, { arg }) => {
        // If this is a new request (no url provided), return only the new data
        if (!arg.url) return newItems;
        
        // For pagination requests, merge with existing cache
        if (!currentCache) return newItems;
        return {
          ...newItems,
          response: {
            ...newItems.response,
            data: {
              ...newItems.response.data,
              runs: [
                ...(currentCache.response.data?.runs || []),
                ...(newItems.response.data?.runs || []),
              ],
            },
          },
        };
      },
    }),
    restoreWorkflowVersion: build.mutation<
      ApiResponse<WorkflowResponseSchema>,
      { workflowId: string; wfRunId: string }
    >({
      query: ({ workflowId, wfRunId }) => ({
        url: WorkflowEndpoints.getWorkflow(workflowId), 
        method: "PUT",
        ignoreBaseUrl: true,
        params: {
          ...(wfRunId && { workflow_run_id: wfRunId }),
          action: "restore",
        },
      }),
      invalidatesTags: ["wf-versions"],
    }),
    renameWorkflowVersion: build.mutation<
      ApiResponse<WorkflowResponseSchema>,
      { workflowId: string; wfRunId: string; body: any }
    >({
      query: ({ workflowId, wfRunId, body }) => ({
        url: WorkflowEndpoints.getWorkflow(workflowId),
        method: "PUT",
        ignoreBaseUrl: true,
        params: {
          workflow_run_id: wfRunId,
          action: "update_workflow_run_metadata",
        },
        body,
      }),
      // util function to handle the rename workflow version optimistically
      onQueryStarted: handleRenameWorkflowVersion,
    }),
  }),
});
export const {
  useGetWorkflowQuery,
  useLazyGetWorkflowQuery,
  useGetWFActiveUserQuery,
  useLazyGetWFActiveUserQuery,
  useRequestEditAccessMutation,
  useEndEditAccessMutation,
  useGetworkflowsFromAnalysisQuery,
  useLazyGetworkflowsFromAnalysisQuery,
  useSaveWorkflowMutation,
  useCreateWorkflowMutation,
  useGetWorkflowRunStatusQuery,
  useRunWorkflowMutation,
  useMarkOutputMutation,
  useUnMarkOutputMutation,
  useTerminateWorkflowMutation,
  useFixWorkflowMutation,
  useRunNodeMutation,
  useTerminateNodeRunMutation,
  useGetWorkflowVersionsQuery,
  useLazyGetWorkflowVersionsQuery,
  useRestoreWorkflowVersionMutation,
  useRenameWorkflowVersionMutation,
  useGetWorkflowEventsQuery,
  useLazyGetWorkflowEventsQuery,
  useClearWorkflowEventsMutation,
} = workflowManagerApi;
